import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { ListResponse } from '@verantos-dev/types-common';

import { authFetch } from './authFetch';
import { projectPageKeys } from './queryKeys';
import type { ProjectArtifactEntry } from './types';

interface DeleteAnalyticalDatasetInput {
  projectId: string;
  analyticalDatasetProfileId: string;
}

/**
 * Delete an analytical-dataset profile (and all its run history) from the
 * artifacts page. Duplicates the DELETE in feature-analytical-datasets on
 * purpose — that lib can't be imported here (dependency cycle). A run in
 * progress returns 409, surfaced to the caller.
 */
export const useDeleteAnalyticalDatasetMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, DeleteAnalyticalDatasetInput>({
    mutationFn: ({ projectId, analyticalDatasetProfileId }) =>
      authFetch<void>(
        `/analytical-datasets/${encodeURIComponent(projectId)}` +
          `/analytical-dataset-profile/${encodeURIComponent(analyticalDatasetProfileId)}`,
        { method: 'DELETE' }
      ),
    onSuccess: (_data, { projectId, analyticalDatasetProfileId }) => {
      // Optimistically drop the deleted row from the manifest cache so the list
      // updates instantly, instead of waiting for the refetch round-trip below.
      const manifestKey = projectPageKeys.artifactsForProject(projectId);
      queryClient.setQueryData<ListResponse<ProjectArtifactEntry>>(manifestKey, (old) =>
        old ? { ...old, data: old.data.filter((e) => e.id !== analyticalDatasetProfileId) } : old
      );
      // The dataset's PROJECT_ARTIFACT row is detached server-side, so reconcile
      // the manifest (and refresh the activity feed) against the server.
      queryClient.invalidateQueries({ queryKey: manifestKey });
      queryClient.invalidateQueries({
        queryKey: projectPageKeys.activityForProject(projectId),
      });
    },
  });
};
